// Quick Actions (System Action Buttons) View (WP-07).
// Manages the 9 system action buttons per-number: get_started, no_match, location_reply,
// unsubscribe, resubscribe, chat_with_human, chat_with_bot, confirm_order, cancel_order.
import { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../../lib/i18n';
import { cn } from '../../lib/cn';
import {
  systemActionList,
  systemActionSave,
  type SystemActionDoc,
  type SystemActionKey,
} from '../../lib/bots';
import SystemActionEditorModal from './SystemActionEditorModal';
import ResetActionsConfirmDialog from './ResetActionsConfirmDialog';
import Toggle from '../ui/Toggle';
import { Button } from '../ui/Button';
import { RefreshIcon, EditIcon } from '../icons';
import { useToast } from '../ui/Toast';

interface QuickActionsViewProps {
  integrationId: string;
  botLabel?: string;
}

const ACTION_ICONS: Record<SystemActionKey, string> = {
  get_started: '👋',
  no_match: '❓',
  location_reply: '📍',
  unsubscribe: '🛑',
  resubscribe: '▶️',
  chat_with_human: '🧑‍💼',
  chat_with_bot: '🤖',
  confirm_order: '✅',
  cancel_order: '❌',
};

export default function QuickActionsView({
  integrationId,
  botLabel,
}: QuickActionsViewProps) {
  const { t } = useI18n();
  const toast = useToast();
  const [actions, setActions] = useState<SystemActionDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingAction, setEditingAction] = useState<SystemActionDoc | null>(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [togglingKey, setTogglingKey] = useState<string | null>(null);

  const loadActions = useCallback(async () => {
    if (!integrationId) return;
    setLoading(true);
    try {
      const res = await systemActionList({ integrationId });
      if (res.data?.ok && Array.isArray(res.data.actions)) {
        setActions(res.data.actions);
      }
    } catch (err) {
      console.error('[QuickActionsView] load error', err);
      toast.error((err as Error).message || 'Failed to load system actions');
    } finally {
      setLoading(false);
    }
  }, [integrationId]);

  useEffect(() => {
    loadActions();
  }, [loadActions]);

  const handleToggle = async (action: SystemActionDoc) => {
    setTogglingKey(action.key);
    const nextEnabled = !action.enabled;
    try {
      const res = await systemActionSave({
        integrationId,
        action: {
          key: action.key,
          enabled: nextEnabled,
          kind: action.kind,
          text: action.text,
          templateName: action.templateName,
          templateLanguage: action.templateLanguage,
          flowId: action.flowId,
        },
      });
      if (res.data?.ok) {
        setActions((prev) =>
          prev.map((a) => (a.key === action.key ? { ...a, enabled: nextEnabled } : a))
        );
        toast.success(
          nextEnabled
            ? t('bots.actions.enabledToast') || 'Action enabled'
            : t('bots.actions.disabledToast') || 'Action disabled'
        );
      }
    } catch (err) {
      console.error('[QuickActionsView] toggle error', err);
      toast.error((err as Error).message || 'Failed to update action');
    } finally {
      setTogglingKey(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-white p-4 dark:border-slate-800 dark:bg-slate-900 shadow-sm">
        <div>
          <h2 className="text-base font-bold text-ink dark:text-white">
            {t('bots.actions.title') || 'Quick Actions (System Action Buttons)'}
          </h2>
          <p className="mt-0.5 text-xs text-ink-3 dark:text-slate-400">
            {t('bots.actions.subtitle') ||
              'Standard system triggers such as welcome greeting, fallback no-match, opt-out/in, and human handoff.'}
            {botLabel && <span className="font-semibold text-brand ms-1">({botLabel})</span>}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={loadActions}
            loading={loading}
            leadingIcon={<RefreshIcon className="h-4 w-4" />}
          >
            {t('common.refresh') || 'Refresh'}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setResetDialogOpen(true)}
            className="text-danger hover:bg-danger/10 hover:border-danger/30"
          >
            {t('bots.actions.resetAll') || 'Reset to Factory Defaults'}
          </Button>
        </div>
      </div>

      {/* Action Cards Grid */}
      {loading && actions.length === 0 ? (
        <div className="grid h-48 place-items-center rounded-2xl border border-line bg-white dark:border-slate-800 dark:bg-slate-900">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" />
        </div>
      ) : (
        <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
          {actions.map((act) => {
            const title = t(`bots.actions.${act.key}.title`) || act.key;
            const desc = t(`bots.actions.${act.key}.desc`) || '';
            const icon = ACTION_ICONS[act.key] || '⚡';
            const isStoreAction = act.key === 'confirm_order' || act.key === 'cancel_order';

            let snippet = '';
            if (act.kind === 'text') {
              snippet = act.text || t('bots.actions.noMessageConfigured') || '(No message text)';
            } else if (act.kind === 'template') {
              snippet = `Template: ${act.templateName || '—'} (${act.templateLanguage || 'en'})`;
            } else if (act.kind === 'flow') {
              snippet = `Flow ID: ${act.flowId || '—'}`;
            }

            return (
              <div
                key={act.key}
                className={cn(
                  'flex flex-col justify-between rounded-2xl border p-4 transition-all shadow-sm',
                  act.enabled
                    ? 'border-line bg-white dark:border-slate-800 dark:bg-slate-900'
                    : 'border-dashed border-slate-200 bg-slate-50/70 opacity-75 dark:border-slate-800 dark:bg-slate-900/40'
                )}
              >
                <div>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                      <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-xl bg-brand/10 text-lg" aria-hidden>
                        {icon}
                      </span>
                      <div>
                        <h3 className="text-sm font-bold text-ink dark:text-white leading-tight">
                          {title}
                        </h3>
                        <div className="mt-1 flex items-center gap-1.5">
                          <span
                            className={cn(
                              'rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
                              act.kind === 'text'
                                ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300'
                                : act.kind === 'template'
                                ? 'bg-purple-50 text-purple-700 dark:bg-purple-950/50 dark:text-purple-300'
                                : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300'
                            )}
                          >
                            {act.kind}
                          </span>
                          {isStoreAction && (
                            <span className="rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-950/50 dark:text-amber-300">
                              WP-12
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className={cn(togglingKey === act.key && 'opacity-50 pointer-events-none')}>
                      <Toggle
                        on={act.enabled}
                        onChange={() => handleToggle(act)}
                        label={title}
                      />
                    </div>
                  </div>

                  {desc && (
                    <p className="mt-2.5 text-xs text-ink-3 dark:text-slate-400 line-clamp-2">
                      {desc}
                    </p>
                  )}

                  <div className="mt-3 rounded-xl bg-surface/70 p-2.5 dark:bg-slate-800/50">
                    <p className="text-xs text-ink-2 dark:text-slate-300 line-clamp-2 font-mono text-[11px]">
                      {snippet}
                    </p>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-line/60 dark:border-slate-800/60 flex items-center justify-end">
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => setEditingAction(act)}
                    leadingIcon={<EditIcon className="h-3.5 w-3.5" />}
                  >
                    {t('bots.actions.edit') || 'Edit Action'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Editor Modal */}
      {editingAction && (
        <SystemActionEditorModal
          open={Boolean(editingAction)}
          action={editingAction}
          integrationId={integrationId}
          onClose={() => setEditingAction(null)}
          onSaved={loadActions}
        />
      )}

      {/* Reset Confirmation Dialog */}
      {resetDialogOpen && (
        <ResetActionsConfirmDialog
          open={resetDialogOpen}
          integrationId={integrationId}
          onClose={() => setResetDialogOpen(false)}
          onReset={() => {
            setResetDialogOpen(false);
            loadActions();
          }}
        />
      )}
    </div>
  );
}
